import React, { useState, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, Pause, Play } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

interface SpeechControlsProps {
  text: string;
  label?: string;
}

export function SpeechControls({ text, label = 'Read Aloud' }: SpeechControlsProps) {
  const { selectedVoice, speechRate } = useSettings();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const voiceName = selectedVoice?.name || 'US English Female'; // Fallback

  const togglePlay = useCallback(() => {
    if (!window.responsiveVoice) {
      console.error('ResponsiveVoice not loaded');
      return;
    }

    if (isPlaying) {
      window.responsiveVoice.pause();
      setIsPlaying(false);
    } else {
      if (window.responsiveVoice.isPlaying()) {
        window.responsiveVoice.resume();
        setIsPlaying(true);
      } else {
        window.responsiveVoice.cancel();
        window.responsiveVoice.speak(text, voiceName, {
          rate: speechRate,
          pitch: 0.9,
          volume: isMuted ? 0 : 1,
          onstart: () => setIsPlaying(true),
          onend: () => setIsPlaying(false),
          onpause: () => setIsPlaying(false),
        });
      }
    }
  }, [isPlaying, text, voiceName, speechRate, isMuted]);

  const toggleMute = useCallback(() => {
    if (!window.responsiveVoice) return;

    setIsMuted((prev) => !prev);
    if (isPlaying) {
      window.responsiveVoice.cancel();
      window.responsiveVoice.speak(text, voiceName, {
        rate: speechRate,
        pitch: 0.9,
        volume: !isMuted ? 0 : 1,
        onstart: () => setIsPlaying(true),
        onend: () => setIsPlaying(false),
      });
    }
  }, [isMuted, text, voiceName, speechRate, isPlaying]);

  useEffect(() => {
    return () => {
      if (window.responsiveVoice) {
        window.responsiveVoice.cancel();
      }
    };
  }, []);

  return (
    <div className="flex items-center space-x-2">
      <button
        onClick={togglePlay}
        disabled={!window.responsiveVoice}
        className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors disabled:opacity-50"
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        <span className="text-sm">{label}</span>
      </button>
      <button
        onClick={toggleMute}
        disabled={!window.responsiveVoice}
        className={`p-1.5 rounded-lg transition-colors ${
          isMuted
            ? 'bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-800'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
      >
        {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
}